import { useEffect, useMemo, useRef, useState } from 'react';
import { getCatalog } from '../../api/catalog';
import { csrf } from '../../api/auth';
import { createProduct } from '../../api/admin/products.admin';
import { fetchBrands } from '../../api/product';
import FeatureFields from './FeatureFields';
import './ProductCreate.css';

function fileWithPreview(file) {
  return Object.assign(file, {
    preview: URL.createObjectURL(file),
    mediaType: String(file.type || '').startsWith('video/') ? 'video' : 'image',
  });
}

const EMPTY_FORM = {
  title: '',
  brand: '',
  sku: '',
  color: '',
  country: '',
  priceRetail: '',
  priceWholesale: '',
  costPrice: '',
  weightKg: '',
  lengthMm: '',
  widthMm: '',
  heightMm: '',
  shortDesc: '',
  fullDesc: '',
  warrantyMonths: '',
};

export default function ProductCreate() {
  const [brandList, setBrandList] = useState([]);
  const [cats, setCats] = useState([]);
  const [topSlug, setTopSlug] = useState('');
  const [subId, setSubId] = useState('');
  const [features, setFeatures] = useState([]);
  const [attributes, setAttributes] = useState({});
  const [primaryIndex, setPrimaryIndex] = useState(0);
  const [brandOpen, setBrandOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [images, setImages] = useState([]);
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const t = setTimeout(async () => {
      try {
        const q = form.brand?.trim() || '';
        const hasQuery = q.length >= 1;
        const list = await fetchBrands({
          topCategorySlug: hasQuery ? undefined : topSlug,
          q: hasQuery ? q : undefined,
        });
        if (!mounted) return;
        setBrandList((list || []).slice(0, 200));
      } catch (_) { /* ignore */ }
    }, 200);

    return () => { mounted = false; clearTimeout(t); };
  }, [topSlug, form.brand]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const data = await getCatalog();
      if (!mounted) return;
      setCats(data || []);
      if (data?.length) setTopSlug(data[0].slug);
    })();
    return () => { mounted = false; };
  }, []);

  const subcats = useMemo(() => {
    const c = cats.find((c) => c.slug === topSlug);
    return c?.subcategories || [];
  }, [cats, topSlug]);

  useEffect(() => {
    const sub = subcats.find((s) => String(s._id) === String(subId));
    const fs = sub?.features || [];
    setFeatures(fs);
    setAttributes((prev) => {
      const next = {};
      fs.forEach((f) => {
        if (prev[f.code] !== undefined && prev[f.code] !== null && prev[f.code] !== '') {
          next[f.code] = prev[f.code];
        } else if (typeof prev[f.code] === 'boolean') {
          next[f.code] = prev[f.code];
        } else {
          next[f.code] = '';
        }
      });
      return next;
    });
  }, [subId, subcats]);

  const onChange = (e) => {
    const { name, type, value, checked } = e.target;
    let v = value;
    if (type === 'number') v = value === '' ? '' : Number(value);
    if (type === 'checkbox') v = checked;
    setForm((f) => ({ ...f, [name]: v }));
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || [])
      .filter((f) => f.type?.startsWith('image/') || f.type?.startsWith('video/'))
      .slice(0, 10)
      .map(fileWithPreview);
    if (!files.length) return;
    setImages((prev) => {
      if (!prev.length) setPrimaryIndex(0);
      return [...prev, ...files].slice(0, 10);
    });
  };

  const onFiles = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const move = (from, to) => {
    setImages((prev) => {
      const arr = [...prev];
      const [f] = arr.splice(from, 1);
      arr.splice(to, 0, f);
      return arr;
    });
    setPrimaryIndex((idx) => {
      if (from === idx) return to;
      if (from < idx && to >= idx) return idx - 1;
      if (from > idx && to <= idx) return idx + 1;
      return idx;
    });
  };

  const removeAt = (i) => {
    setImages((prev) => {
      const arr = [...prev];
      const [removed] = arr.splice(i, 1);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return arr;
    });
    setPrimaryIndex((idx) => {
      if (i === idx) return 0;
      if (i < idx) return idx - 1;
      return idx;
    });
  };

  const clearImages = () => {
    images.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    setImages([]);
    setPrimaryIndex(0);
  };

  const onTopChange = (e) => {
    setTopSlug(e.target.value);
    setSubId('');
    setFeatures([]);
    setAttributes({});
  };
  const onSubChange = (e) => setSubId(e.target.value);

  const submit = async (e) => {
    e.preventDefault();
    setMsg('');
    setMsgOk(false);
    setSaving(true);
    try {
      if (!topSlug || !subId) {
        setMsg('Выберите категорию и подкатегорию');
        setSaving(false);
        return;
      }

      await csrf();

      await createProduct({
        ...form,
        topCategorySlug: topSlug,
        subcategoryId: subId,
        attributes,
        images,
        primaryIndex,
      });

      setMsg('Товар создан');
      setMsgOk(true);
      setForm(EMPTY_FORM);
      clearImages();
      setAttributes({});
      setFeatures([]);
      setSubId('');
    } catch (err) {
      setMsg(err?.response?.data?.error || 'Ошибка сохранения');
      setMsgOk(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pc-page">
      <h1 className="pc-title admin-page-title">Новый товар</h1>
      <p className="pc-lead">Заполните основные поля, цены и фото — остальное можно дописать позже.</p>
      {msg && <div className={`pc-msg ${msgOk ? 'is-ok' : 'is-err'}`}>{msg}</div>}

      <form className="pc-form" onSubmit={submit}>
        <section className="pc-card">
          <h3 className="pc-card__title">Основное</h3>
          <div className="grid two">
            <label className="pc-span-2">
              <span className="req">Название</span>
              <input name="title" value={form.title} onChange={onChange} required autoFocus placeholder="Например: Смеситель для раковины" />
            </label>

            <label className="pc-field-wrap">
              Бренд
              <input
                name="brand"
                value={form.brand}
                onChange={(e) => { onChange(e); setBrandOpen(true); }}
                onFocus={() => setBrandOpen(true)}
                onBlur={() => setTimeout(() => setBrandOpen(false), 150)}
                placeholder="Начните вводить…"
                autoComplete="off"
              />
              {brandOpen && brandList.length > 0 && (
                <ul className="pc-suggest">
                  {brandList.map((b) => (
                    <li key={b}>
                      <button
                        type="button"
                        onClick={() => { setForm((f) => ({ ...f, brand: b })); setBrandOpen(false); }}
                      >
                        {b}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {form.brand && !brandList.some((b) => b.toLowerCase() === String(form.brand).toLowerCase()) && (
                <div className="pc-hint">Будет создан новый бренд «{form.brand}»</div>
              )}
            </label>

            <label>
              Артикул
              <input name="sku" value={form.sku} onChange={onChange} placeholder="SKU / артикул" />
            </label>

            <label>
              Цвет
              <input name="color" value={form.color} onChange={onChange} placeholder="например Хром" />
            </label>

            <label>
              Страна
              <input name="country" value={form.country} onChange={onChange} placeholder="например Германия" />
            </label>

            <label>
              Гарантия, мес.
              <input name="warrantyMonths" type="number" min="0" step="1" value={form.warrantyMonths} onChange={onChange} placeholder="12" />
            </label>

            <label>
              <span className="req">Категория</span>
              <select value={topSlug} onChange={onTopChange}>
                {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name?.ru || c.slug}</option>)}
              </select>
            </label>

            <label>
              <span className="req">Подкатегория</span>
              <select value={subId} onChange={onSubChange} required disabled={!subcats.length}>
                <option value="">{subcats.length ? '— выберите —' : 'Нет подкатегорий'}</option>
                {subcats.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name?.ru || s.slug}
                  </option>
                ))}
              </select>
            </label>

            <label className="pc-span-2">
              Полное описание
              <textarea name="fullDesc" value={form.fullDesc} onChange={onChange} placeholder="Характеристики, комплектация, особенности…" />
            </label>
          </div>
        </section>

        <section className="pc-card">
          <h3 className="pc-card__title">Цены и склад</h3>
          <div className="grid three">
            <label>
              <span className="req">Розничная цена</span>
              <input name="priceRetail" type="number" min="0" step="1" value={form.priceRetail} onChange={onChange} required />
            </label>
            <label>
              <span className="req">Оптовая цена</span>
              <input name="priceWholesale" type="number" min="0" step="1" value={form.priceWholesale} onChange={onChange} required />
            </label>
            <label>
              Закупочная цена
              <input name="costPrice" type="number" min="0" step="1" value={form.costPrice} onChange={onChange} />
            </label>
          </div>
          <p className="pc-help">Количество на склад добавляйте отдельно в разделе «Склад».</p>
        </section>

        <section className="pc-card">
          <h3 className="pc-card__title">Габариты и вес</h3>
          <div className="grid four">
            <label>
              Вес, кг
              <input name="weightKg" type="number" min="0" step="0.01" value={form.weightKg} onChange={onChange} placeholder="12.5" />
            </label>
            <label>
              Длина, мм
              <input name="lengthMm" type="number" min="0" step="1" value={form.lengthMm} onChange={onChange} placeholder="700" />
            </label>
            <label>
              Ширина, мм
              <input name="widthMm" type="number" min="0" step="1" value={form.widthMm} onChange={onChange} placeholder="400" />
            </label>
            <label>
              Высота, мм
              <input name="heightMm" type="number" min="0" step="1" value={form.heightMm} onChange={onChange} placeholder="350" />
            </label>
          </div>
        </section>

        {subId && (
          <section className="pc-card">
            <h3 className="pc-card__title">Особенности</h3>
            <p className="pc-help">Все поля необязательны. В списке можно выбрать «Своё значение» — оно сохранится и появится в фильтрах.</p>
            <FeatureFields features={features} attributes={attributes} onChange={setAttributes} />
          </section>
        )}

        <section className="pc-card">
          <h3 className="pc-card__title">Фото и видео</h3>
          <p className="pc-help">До 10 файлов. Клик по ⭐ — главное. Видео сжимается на сервере (mp4 / webm / mov).</p>

          <div
            className={`pc-drop ${dragOver ? 'is-drag' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => fileRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileRef.current?.click();
              }
            }}
            onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              addFiles(e.dataTransfer.files);
            }}
          >
            <input ref={fileRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" multiple onChange={onFiles} />
            <div className="pc-drop__title">Перетащите фото или видео сюда или нажмите, чтобы выбрать</div>
            <div className="pc-drop__hint">
              {images.length ? `Выбрано: ${images.length} из 10` : 'JPG, PNG, WEBP, MP4, WEBM, MOV'}
            </div>
          </div>

          {images.length > 0 && (
            <>
              <div className="pc-images__list">
                {images.map((f, i) => (
                  <div key={i} className={`pc-thumb ${i === primaryIndex ? 'is-main' : ''}`}>
                    {f.mediaType === 'video' ? (
                      <video src={f.preview} muted playsInline preload="metadata" />
                    ) : (
                      <img src={f.preview} alt={`img-${i}`} />
                    )}
                    <div className="pc-thumb__actions">
                      <button type="button" onClick={() => setPrimaryIndex(i)} title="Сделать главной">⭐</button>
                      <button type="button" disabled={i === 0} onClick={() => move(i, i - 1)} title="Влево">↑</button>
                      <button type="button" disabled={i === images.length - 1} onClick={() => move(i, i + 1)} title="Вправо">↓</button>
                      <button type="button" onClick={() => removeAt(i)} title="Удалить">✕</button>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className="btn-ghost" onClick={clearImages}>Очистить медиа</button>
            </>
          )}
        </section>

        <div className="pc-actions">
          <button disabled={saving} type="submit" className="btn-primary">
            {saving ? 'Сохраняю…' : 'Создать товар'}
          </button>
        </div>
      </form>
    </div>
  );
}
